import { FC } from 'react';

import closeIcon from 'assets/icons/Close.svg';

import Portal from './Portal';
import SvgIcon from 'shared/components/SvgIcon/SvgIcon';

// CSS modules
import Modal from './modal.module.scss';
import Icons from 'styles/icons.module.scss';

interface IModal {
	close: () => void;
	render: any;
	title: string;
	icon?: string;
	bigModal?: boolean;
	children?: React.ReactNode;
}

/**
 * Standard application modal with a title bar, optional icon, close button, and backdrop.
 *
 * Renders its content via a `render` prop pattern, which allows callers to inject
 * content without needing to compose additional wrapper components.
 * Pass `bigModal` to render in a wider container.
 *
 * Mounts via {@link Portal} so it always appears above the page layout.
 *
 * @example
 * <Remodal title="Edit User" close={handleClose} render={(children) => children}>
 *   <UserForm />
 * </Remodal>
 */
const Remodal: FC<IModal> = ({ children, close, render, title, icon, bigModal }) => {
	return (
		<Portal>
			<div className={Modal.modalRoot}>
				<div className={bigModal ? Modal.modalBigWrapper : Modal.modalWrapper}>
					<div className={Modal.modalHeader}>
						<div className={Modal.modalTitle}>
							{icon && <SvgIcon spriteUrl={icon} className={Icons.modalIcon} />}
							<h4>{title}</h4>
						</div>
						<SvgIcon spriteUrl={closeIcon} className={Modal.modalCloseIcon} onClick={close} />
					</div>
					{bigModal ? (
						<div className={Modal.modalBigContent}>
							{render(children) || children}
						</div>
					) : (
						render(children) || children
					)}
				</div>
				<div className={Modal.modalBackdrop} onClick={close} />
			</div>
		</Portal>
	);
};

export default Remodal;
